import React, { useMemo } from "react";
import { Dropdown, Input, Avatar, Spin, Button } from "antd";
import { Icon } from "@iconify/react";
import { useNavigate } from "react-router-dom";
import { useAuth, useLogout } from "../../hooks/useAuth";
import { usePermissions } from "../../context/PermissionContext";
import { useUserLocation } from "../../hooks/useLocation";
import NotificationBell from "../notifications/NotificationBell";
import Logo from "../../asset/images/Area-56-logo.png";

const Navbar: React.FC<{ toggleSidebar: () => void }> = ({ toggleSidebar }) => {
  const navigate = useNavigate();
  const { data: user } = useAuth();
  const { userRole } = usePermissions();
  const { mutate: logout } = useLogout();
  const { data: userLocation, isLoading: locationLoading } = useUserLocation(
    user?.location?.id
  );

  const userTypeId = useMemo(
    () => Number(user?.user_type_id) || 3,
    [user?.user_type_id]
  );

  const profileMenu = (
    <div className="bg-white shadow-md rounded-lg border w-48">
      <div
        className="px-4 py-2 cursor-pointer hover:bg-gray-100 transition-colors"
        onClick={() => navigate("/profile")}
      >
        <div className="flex items-center space-x-2">
          <Icon icon="heroicons:user" className="text-gray-500" />
          <span>Profile</span>
        </div>
      </div>
      <div
        className="px-4 py-2 text-red-600 cursor-pointer hover:bg-red-50 transition-colors"
        onClick={() => logout()}
      >
        <div className="flex items-center space-x-2">
          <Icon
            icon="heroicons:arrow-right-on-rectangle"
            className="text-red-600"
          />
          <span>Log Out</span>
        </div>
      </div>
    </div>
  );

  return (
    <div className="flex items-center justify-between px-6 py-3 bg-primary shadow-sm">
      {/* Left Section - Logo & Sidebar Toggle */}
      <div className="flex items-center space-x-4">
        {/* Sidebar Toggle Button */}
        <Button
          type="text"
          onClick={toggleSidebar}
          className="md:hidden text-white hover:bg-white hover:bg-opacity-20"
        >
          <Icon icon="heroicons:bars-3" className="text-xl" />
        </Button>

        {/* Logo */}
        <div className="text-lg font-bold flex items-center space-x-2 pl-6 md:pl-0">
          <img src={Logo} alt="AREA 56 Logo" className="w-7 bg-primary" />
          <span className="text-xs text-white">Admin</span>
        </div>
      </div>

      {/* Middle Section - Location & Search */}
      <div className="hidden md:flex items-center space-x-6">
        <div className="flex items-center text-secondary">
          <Icon icon="heroicons:map-pin" className="mr-1 text-lg" />
          {locationLoading ? (
            <Spin size="small" />
          ) : (
            <span className="text-sm opacity-90">
              {userLocation || "Unknown Location"}
            </span>
          )}
        </div>

        {/* Search Bar */}
        <Input.Search
          placeholder="Search for Employee, Order ID"
          allowClear
          className="rounded-lg bg-gray-100 hidden md:block"
          style={{ width: "300px" }}
        />
      </div>

      {/* Right Section - Notifications & Profile */}
      <div className="flex items-center space-x-4 text-gray-100">
        {/* Notifications Bell */}
        <div className="notification-bell-wrapper">
          <NotificationBell />
        </div>

        <div className="h-6 w-px bg-gray-300 hidden md:block opacity-30"></div>

        {/* User Profile Dropdown */}
        <Dropdown
          overlay={profileMenu}
          trigger={["click"]}
          placement="bottomRight"
        >
          <div className="flex items-center space-x-3 cursor-pointer hover:bg-white hover:bg-opacity-10 rounded-lg px-2 py-1 transition-all duration-200">
            <Avatar
              src={
                user?.avatar
                  ? user?.avatar
                  : user?.user_type?.icon ?? "https://via.placeholder.com/150"
              }
              size="large"
              className="cursor-pointer bg-gray-100 border-2 border-white border-opacity-20"
            />
            <div className="hidden md:block">
              <div className="font-semibold text-sm  text-white">
                {user?.name || "Unknown User"}
              </div>
              <div className="text-xs opacity-75 text-secondary">
                {userRole || `User Type ${userTypeId}`}
              </div>
            </div>
            <Icon
              icon="heroicons:chevron-down"
              className="text-white opacity-75 text-sm hidden md:block"
            />
          </div>
        </Dropdown>
      </div>
    </div>
  );
};

export default Navbar;
